import { DateRangeObject } from '@acceso/design-system';
import { InfiniteData } from '@tanstack/react-query';

import {
  TProgramBroadcastSegmentsParameters,
  TProgramBroadcastSummary,
} from '@/features/broadcast-schedule/types';
import { TClipLogo, TClipPeople } from '@/features/clips/types';
import { TProgram } from '@/features/programs/types';
import { EReviewIncidenceType, TRect } from '@/types';
import { TPresenceItem } from '@/types/presences';
import { EVisibility } from '@/types/types';

export type TSegmentParameters = {
  start_frame?: number;
  end_frame?: number;
  bbox: TRect;
  landmarks?: string[];
  support_id?: string;
  visibility?: EVisibility;
  is_manual?: boolean;
  start_ts?: string;
  end_ts?: string;
  hash?: string;
  support_code?: string;
  segment_id?: string;
};

export type TSegmentCommon = {
  id?: string;
  entity_label: string;
  entity_score: number;
  segment_id: string;
  start_datetime: string;
  end_datetime: string;
  parameters: TSegmentParameters;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  review_state: 0 | 1;
};

export type TSegmentLogo = TSegmentCommon & {
  kind: 'logo';
  logo_id: string;
  children?: TSegmentLogo[];
  support?: string;
  thumbnail_url?: string;
};

export type LogoWithChildren<T extends TPresenceItem> = T & { children: T[] };

export type TSegmentPeople = TSegmentCommon & {
  kind: 'people';
  people_id: string;
  children?: TSegmentPeople[];
};

export type TSegmentPresence = TSegmentLogo | TSegmentPeople;
export type TClipPresence = TClipLogo | TClipPeople;

/**
 * Derives the kind discriminant for clip presences that may lack a `kind` field
 * at runtime (the API does not return it). Uses the structural difference between
 * TClipLogo (`logo_id`) and TClipPeople (`people_id`) as the source of truth.
 */
export const getClipPresenceKind = (presence: TClipPresence): 'logo' | 'people' =>
  'logo_id' in presence ? 'logo' : 'people';

/**
 * Derives the kind discriminant for segment presences that may lack a `kind` field
 * at runtime (the API does not return it). Uses the structural difference between
 * TSegmentLogo (`logo_id`) and TSegmentPeople (`people_id`) as the source of truth.
 */
export const getSegmentPresenceKind = (presence: TSegmentPresence): 'logo' | 'people' =>
  'logo_id' in presence ? 'logo' : 'people';

export type TBaseItemMention = {
  start_datetime: string;
  end_datetime: string;

  /**
   * List of keywords.
   *
   * @example ['Madrid']
   */
  keywords: string[];
};

export type TSegmentMention = TBaseItemMention & {
  id: string;
  segment_id: string;

  /**
   * List of client IDs.
   */
  clients: string[];

  /**
   * List of workspace IDs.
   */
  workspaces: string[];

  /**
   * List of information unit tag IDs.
   */
  tags: string[] | null;

  text: string;
  query_id?: string;
  created_at: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
};

export type TSegmentTranscriptions = {
  id: string;
  segment_id: string;
  asr_recognition_results: TASRRecognitionResult[];
  created_at: string;
  created_by: string;
  updated_at: null;
  updated_by: null;
  parameters: TSegmentTranscriptionsParameters;
};

export type TASRRecognitionResult = {
  id: number;
  endOffset: number;
  startOffset: number;
  text: string;
  speakerLabel: string;
};

export type TAlternative = {
  content: string;
  language: LanguageEnum;
  confidence: number;
  speakerLabel: SpeakerLabelEnum;
};

export enum LanguageEnum {
  Es = 'es',
}

export enum SpeakerLabelEnum {
  S1 = 'S1',
}

export enum TASRRecognitionResultType {
  Punctuation = 'punctuation',
  Word = 'word',
}

export enum StatuEnrichmentEnum {
  PROCESSING = 'processing',
  PROCESSED = 'processed',
  REPROCESSING = 'reprocessing',
  REPROCESSED = 'reprocessed',
  ERROR = 'error',
  NOT_PROCESSED = 'not_processed',
}

export enum GlobalStatusEnrichmentEnum {
  PROCESSING = 'processing',
  PROCESSED = 'processed',
  ERROR = 'error',
  NOT_PROCESSED = 'not_processed',
}

export enum ClipSyncStatusEnum {
  WAITING_ENRICHMENTS = 'waiting_enrichments',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export type TStatusEnrichment = {
  status: StatuEnrichmentEnum;
  updated_at: string;
};

export type TStatusEnrichments = {
  stt: TStatusEnrichment;
  logos: TStatusEnrichment;
  peoples: TStatusEnrichment;
  ocr: TStatusEnrichment;
  advertising: TStatusEnrichment;
};

export type TSegmentTranscriptionsParameters = {
  mock: null;
  status_enrichments: TStatusEnrichment;
};

/**
 * Enum representing the media types for a segment.
 */
export enum SegmentMediaTypeEnum {
  /**
   * TV segment media type.
   */
  SEGMENT_TV = 'tvts',

  /**
   * Radio segment media type.
   */
  SEGMENT_RADIO = 'rts',
}

/* 0- No revisado
1- Pendiente
2- En revisión
3- Revisado
4- Descartado
5- Incidencia
6- Sin revisión */
export type TReviewStateValues = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export enum ReviewStateEnum {
  REQUIRE_REVIEW = 0,
  PENDING = 1,
  IN_REVIEW = 2,
  REVIEWED = 3,
  REJECTED = 4,
  INCIDENCE = 5,
  WITHOUT_REVIEW = 6,
}

export type TSegment = {
  id: string;
  uri: string;
  channel_id: string;
  media_type: SegmentMediaTypeEnum;
  start_datetime: string;
  end_datetime: string;
  duration_in_seconds: number;
  language_id: string;
  review_state: TReviewStateValues;
  created_at?: string;
  programs: TProgram[];
  segment_mentions: TSegmentMention[];
  segment_transcriptions: TSegmentTranscriptions[];
  segment_declarations: TSegmentDeclaration[];
  segment_ocrs: TSegmentOcr[];
  segment_logos: TSegmentLogo[];
  segment_peoples: TSegmentPeople[];
  created_by?: string;
  updated_at?: string | null;
  updated_by?: string | null;
  reviewed_by_user_id?: string;
  review_incidence_type?: EReviewIncidenceType;
  review_incidence_note?: string;
  presigned_url?: string;
  parameters?: TProgramBroadcastSegmentsParameters;
  audience?: number;
  economical_value?: number;
  audience_rating_percent?: number;
  animation?: 'enter-left' | 'enter-right' | 'exit-left' | 'exit-right';
  global_status_enrichment: GlobalStatusEnrichmentEnum;
  /**
   * The emissions this segment belongs to, one per `parameters.program_values` entry.
   * A segment on a frontier belongs to two. Optional because a segment recorded before
   * the API stated them names none.
   */
  program_broadcasts?: TProgramBroadcastSummary[] | null;
};

export type SegmentFormType = {
  description: string;
  channel_id: string;
  production_center_id: string;
  date_range: DateRangeObject;
  language_id: string;
  media_type: SegmentMediaTypeEnum;
};

export type TParametersBBox = {
  bbox?: TRect;
  landmarks?: string[];
  segment_id?: string;
  visibility?: EVisibility;
  is_manual?: boolean;
  support_id?: string;
};

export type TPostSegmentLogo = {
  segment_id: string;
  entity_score: number;
  start_datetime: Date | null;
  end_datetime: Date | null;
  parameters: TParametersBBox;
  logo_id: string;
  segment_logo_id: string | null;
  review_state: 0 | 1;
  id?: string;
  silently?: boolean;
};

export type TPatchSegmentLogo = {
  entity_score: number;
  start_datetime: Date | null;
  end_datetime: Date | null;
  parameters: TParametersBBox;
  segment_logo_id?: string | null;
  review_state?: 0 | 1;
};

export type TPostSegmentPeople = {
  id: string;
  segment_id: string;
  entity_score: number;
  start_datetime: Date | null;
  end_datetime: Date | null;
  parameters: TParametersBBox;
  people_id: string;
  segment_people_id: string;
  review_state: 0 | 1;
};

export type TPatchSegmentPeople = {
  entity_score: number;
  start_datetime: Date | null;
  end_datetime: Date | null;
  parameters: TParametersBBox;
  segment_people_id?: string;
  people_id?: string;
  review_state?: 0 | 1;
};

export type SegmentFilters = {
  idIn: string[];
  channelIdIn: string[];
  startDatetimeGte: string;
  endDatetimeLte: string;
  createdAtLte: string;
};

export type TSegmentDeclaration = {
  id: string;
  people_id: string | null;
  segment_id: string;
  start_datetime: string;
  end_datetime: string;
  text: string;
  review_state: 0 | 1;
  created_at: string;
  entity_score: number;
  parameters?: TParametersBBox;
  entity_label?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  children?: TSegmentDeclaration[];
};

export type TPostSegmentDeclaration = {
  people_id: string | null;
  start_datetime: Date | null;
  end_datetime: Date | null;
  segment_id: string;
  text: string;
  review_state: 0 | 1;
};

export type TPatchSegmentDeclaration = {
  id: string;
  segment_id?: string;
  people_id?: string | null;
  start_datetime: Date | null;
  end_datetime: Date | null;
  text?: string;
  review_state?: 0 | 1;
};

export type TPatchSegmentTranscription = {
  segment_id?: string | null;
  asr_recognition_results?: TASRRecognitionResult[] | null;
  segment_transcription_id?: string;
};

export type TOcrParameters = {
  hash: string;
};

export type TSegmentOcr = {
  id: string;
  text: string;
  segment_id: string;
  start_datetime: string;
  end_datetime: string;
  created_at: string;
  review_state: 1 | 0;
  parameters: TOcrParameters;
  created_by: string;
  updated_at: string;
  updated_by: string;
  children?: TSegmentOcr[];
};

export type TPostSegmentOcr = {
  segment_id: string;
  start_datetime: Date | null;
  end_datetime: Date | null;
  text: string;
  review_state: 1 | 0;
};

export type TPatchSegmentOcr = {
  id?: string;
  segment_id?: string;
  start_datetime?: Date | null;
  end_datetime?: Date | null;
  text?: string;
  review_state?: 1 | 0;
};

type UpdateSegmentItem = {
  id: string;
  review_state: ReviewStateEnum;
};

type RelaunchSegmentItem = {
  id: string;
};

type UpdateSegmentsBatch = {
  action: 'update';
  items: UpdateSegmentItem[];
};

export type TEnrichmentsSelection = {
  stt: boolean;
  logos: boolean;
  peoples: boolean;
  ocr: boolean;
  advertising: boolean;
};

type RelaunchSegmentsBatch = {
  action: 'relaunch';
  items: RelaunchSegmentItem[];
  enrichments: TEnrichmentsSelection;
};

export type TPostSegmentsBatch = UpdateSegmentsBatch | RelaunchSegmentsBatch;

export type InfiniteSegmentsData = {
  data: TSegment[];
  total: number;
  timestamp: string;
  offset: number;
};

export type InfiniteSegmentsPageParam = {
  offset: number;
  timestamp: string;
};

export type InfiniteSegments = InfiniteData<InfiniteSegmentsData, InfiniteSegmentsPageParam>;
